import { Roles } from "@/services/socket/meeting";
import { MediaTypes } from "@/services/meeting/slides";
import { FeedbackTypes, ActionTypes } from "@/services/meeting/messaging";
import {
  BackendCart,
  PackageListResponse
} from "@/interfaces/packages/sales_packages";

export enum Flows {
  Loading,
  DeviceSelection,
  InVideo
}

export const enum Targets {
  Client = "CLIENT",
  Server = "SERVER",
  Socket = "SOCKET",
  Local = "LOCAL"
}

export interface TokenDetails {
  id: string;
  role: Roles;
  lead: number;
  lead_name: string; // eslint-disable-line @typescript-eslint/camelcase
  name: string;
}

export const EMPTY_TOKEN_DETAILS: TokenDetails = {
  id: "",
  role: Roles.CLIENT,
  lead: 0,
  name: "",
  lead_name: "" // eslint-disable-line @typescript-eslint/camelcase
};

export interface RepTokenDetails {
  id: string;
  role: Roles;
  lead: number;
  lead_name: string; // eslint-disable-line @typescript-eslint/camelcase
  name: string;
}

export interface Media {
  kind: MediaTypes;
  location: string;
}

export interface Action {
  id: string;
  action: ActionTypes;
  name: string;
  clientTitle?: string;
  media?: Media;
  children?: Action[];
  target: Targets;
}

export enum MeetingActivityKind {
  MEETING_STARTED = "MEETING_STARTED",
  MEETING_FINISHED = "MEETING_FINISHED",
  MEETING_COMMENT = "MEETING_COMMENT",
  MEETING_FEEDBACK_COMMENT = "MEETING_FEEDBACK_COMMENT",
  MEETING_NOT_ATTENDED = "MEETING_NOT_ATTENDED"
}

export const END_MEETING_ACTION: Action = {
  id: "end meeting",
  name: "End meeting",
  action: ActionTypes.END_MEETING,
  target: Targets.Server
};

export interface HandRaise {
  userId: string;
  name: string;
}

export interface Slide {
  media?: Media;
  clientTitle: string;
  id: string;
  repPreview?: string;
}

export type Asset = string;

const ActivationTypes = {
  NONE: "NONE",
  OPTIONAL: "OPTIONAL",
  MANDATORY: "MANDATORY"
};

export type ActivationTypes = keyof typeof ActivationTypes;

export interface ExtraButton {
  text: string;
  color?: string;
  confirmed?: boolean;
  icon?: string;
  extra_context?: any;
}

export interface SalesRepTimer {
  timeInSeconds: number;
  actionId: string;
}

export interface MoreDetailsResponseSetting {
  BOOST_NOW_sign_only?: boolean;
  BOOST_NOW_auto_tnc?: boolean;
  BOOST_NOW_has_confirm_button?: boolean;
  BOOST_NOW_waiting_slide?: string;
  BOOST_NOW_sales_rep_timer?: SalesRepTimer;
  BOOST_NOW_extra_buttons?: ExtraButton[];
}

export interface DetailsResponse {
  activation_meeting: ActivationTypes;
  pusher_key: string;
  pusher_cluster: string;
  lead_channel: string;
  lead_boost_now_channel: string;
  actions: Action[];
  assets: Asset[];
  tnc: string;
  tnc_html: string;
  tnc_url: string;
  tnc_before_signature: boolean;
  tnc_can_skip: boolean;
  program_name: string;
  currency: string;
  event_logo_url?: string;
  number_format: string;
  tax_message?: string;
  has_did_not_attend_button?: number;
  has_reserve_button?: number;
  event_id: number;
  event_color: string;
  additional_field: string;
  gipt_schema: object;
  gipt_context_properties: GiptContextProperty[];
  has_blur_video_feature?: number;
}

export type Feedback<T extends Record<string, any>> = {
  kind: FeedbackTypes;
  message?: string;
  meta: T;
};

export enum VimeoMetaTypes {
  PLAYING = "playing",
  TIMEUPDATE = "timeupdate",
  ENDED = "ended"
}

export type VimeoMeta = {
  kind: VimeoMetaTypes;
  value: number | string;
};

export type VimeoFeedback = Feedback<VimeoMeta>;

export interface MyChatEntry {
  userId: string;
  content: string;
  name: string;
}

export interface ChatEntry {
  when: number;
  user_id: string;
  name: string;
  content: string;
}

export type Gipt = { [id: string]: string | string[] };

export interface StateResponse {
  chat: ChatEntry[];
  slide: Slide;
  action: Action;
  roster: BackendMember[];
  cart: BackendCart[];
  signatory: string;
  signature: string;
  tncAccepted: boolean;
  gipt: Gipt;
  giptPackages: PackageListResponse[];
  checklist: CheckList;
}

export interface CheckList {
  videoduration?: number;
  slideview?: number[];
  video?: boolean;
  worksheet?: boolean;
  answers?: { q: number; a: string }[];
}

export interface BackendMember {
  user_id: string;
  name: string;
  chime_user?: string;
}

export type RosterResponse = Pick<StateResponse, "roster">;
export type ChatResponse = Pick<StateResponse, "chat">;
export type SlideResponse = Pick<StateResponse, "slide">;
export type CartResponse = Pick<StateResponse, "cart">;
export type SignatoryResponse = Pick<StateResponse, "signatory">;
export type TncResponse = Pick<StateResponse, "tncAccepted">;
export type GiptFormResponse = Pick<StateResponse, "gipt">;
export type GiptPackagesResponse = Pick<StateResponse, "giptPackages">;

export const MAIN_CHAT_ACTION: Action = {
  id: "main chat me",
  name: "Me",
  action: ActionTypes.MAIN_CHAT,
  clientTitle: "Your dedicated sales representative",
  target: Targets.Server,
  media: {
    kind: MediaTypes.CHAT,
    location: "salesrep"
  }
};

export interface IssueDetails {
  userId: string;
  name: string;
}

export interface CommentEntry {
  comment: string;
  when: string;
}

export interface GiptContextProperty {
  contextPropertyName: string;
  type: ContextPropertyType;
  value1?: string;
  value2?: string;
}

export enum ContextPropertyType {
  EQUAL = "equal",
  HAS_VALUE = "hasValue",
  LOGICAL_OPERATORS = "logicalOperators"
}

export enum LogicalOperator {
  AND = "&&",
  OR = "||"
}
